// Customer list (MUI, /customers-mui). No design mock — applies the established
// system: search + status filter + a flat table. Same data/logic as the old
// /customers page (getHealth, relDate); reskinned in the brain design system.
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  isOpenProjectStatus, listCustomers, listProjects, saveCustomer, type Customer, type Project } from "../../lib/brain-api";
import { useProfile } from "../../lib/profile";
import { brainTokens } from "../../design/brainTheme";
import { KpiCard, SectionCard, StatusPill, Segmented, CustomerAvatar, Icon } from "../../mui";

const c = brainTokens.color;

type Health = "green" | "amber" | "red";
function getHealth(cu: Customer): Health {
  if (cu.status === "churned") return "red";
  const pct = cu.completeness ?? 0;
  if (pct >= 70) return "green";
  if (pct >= 40) return "amber";
  return "red";
}
const HEALTH_RANK: Record<Health, number> = { red: 0, amber: 1, green: 2 };

function ageDays(ts?: number): number | null {
  if (!ts) return null;
  const ms = ts < 1e12 ? ts * 1000 : ts;
  return Math.floor((Date.now() - ms) / 86400000);
}
function relDate(ts?: number): string {
  const d = ageDays(ts);
  if (d == null) return "—";
  if (d <= 0) return "today";
  if (d === 1) return "yesterday";
  if (d < 30) return `${d}d ago`;
  return `${Math.floor(d / 30)}mo ago`;
}
function activityColor(ts?: number): string {
  const d = ageDays(ts);
  if (d == null) return c.faint;
  if (d > 90) return c.dangerText;
  if (d > 30) return c.warnText2;
  return c.muted2;
}

export function CustomerListPageMui() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { profile } = useProfile();
  const isAdmin = profile?.role === "admin";

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [projectMap, setProjectMap] = useState<Map<string, Project[]>>(new Map());
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [healthFilter, setHealthFilter] = useState<"all" | Health>("all");
  const [newOpen, setNewOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [custs, projs] = await Promise.allSettled([listCustomers(), listProjects()]);
      if (custs.status === "fulfilled") setCustomers(custs.value);
      else enqueueSnackbar("Failed to load customers.", { variant: "error" });
      if (projs.status === "fulfilled") {
        const m = new Map<string, Project[]>();
        for (const p of projs.value) {
          if (p.customer) {
            if (!m.has(p.customer)) m.set(p.customer, []);
            m.get(p.customer)!.push(p);
          }
        }
        setProjectMap(m);
      }
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);
  useEffect(() => {
    void load();
  }, [load]);

  const createCustomer = async () => {
    const name = newName.trim();
    if (!name) return;
    const customerId = name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
    setBusy(true);
    try {
      await saveCustomer({ customerId, name, status: "prospect" });
      enqueueSnackbar("Customer created.", { variant: "success" });
      setNewOpen(false);
      setNewName("");
      navigate(`/customers/${encodeURIComponent(customerId)}?tab=overview`);
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : "Create failed.", { variant: "error" });
    } finally {
      setBusy(false);
    }
  };

  const needle = query.trim().toLowerCase();
  const filtered = useMemo(
    () =>
      customers.filter((cu) => {
        if (healthFilter !== "all" && getHealth(cu) !== healthFilter) return false;
        if (needle) {
          const hay = [cu.name, cu.customerId, cu.csm ?? "", cu.tier ?? "", ...cu.product, ...cu.aliases]
            .join(" ")
            .toLowerCase();
          if (!hay.includes(needle)) return false;
        }
        return true;
      }),
    [customers, healthFilter, needle],
  );

  const sorted = useMemo(
    () =>
      [...filtered].sort((a, b) => 
        (a.name ?? "").localeCompare(b.name ?? "", undefined, { sensitivity: "base" }) ||
        (a.completeness ?? 0) - (b.completeness ?? 0)
      ),
    [filtered],
  );

  const kpis = useMemo(() => {
    const total = customers.length;
    const healthy = customers.filter((cu) => getHealth(cu) === "green").length;
    const attention = customers.filter((cu) => getHealth(cu) !== "green").length;
    const withPct = customers.filter((cu) => typeof cu.completeness === "number");
    const avg = withPct.length ? Math.round(withPct.reduce((s, cu) => s + (cu.completeness ?? 0), 0) / withPct.length) : 0;
    return { total, healthy, attention, avg };
  }, [customers]);

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2, mb: 3 }}>
        <Box>
          <Box sx={{ fontSize: 36, fontWeight: 700, letterSpacing: "-.02em", lineHeight: 1 }}>Customers</Box>
          <Box sx={{ fontSize: 14, color: c.muted2, mt: "10px" }}>Knowledge health, files, and projects for every customer</Box>
        </Box>
        {isAdmin && (
          <Button variant="contained" onClick={() => setNewOpen(true)} startIcon={<Icon name="arrow-right" size={15} stroke={2} />}>
            New customer
          </Button>
        )}
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "14px", mb: "20px" }}>
        <KpiCard label="Customers" value={kpis.total} />
        <KpiCard label="Healthy" value={kpis.healthy} valueColor={c.successText} hint={kpis.total ? `${Math.round((kpis.healthy / kpis.total) * 100)}% · completeness ≥ 70%` : undefined} />
        <KpiCard label="Need attention" value={kpis.attention} valueColor={kpis.attention ? c.warnText : c.ink} hint="aging or at-risk" />
        <KpiCard label="Avg completeness" value={`${kpis.avg}%`} valueColor={c.tealInk2} />
      </Box>

      <SectionCard padding={0}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "12px", p: "16px 20px", flexWrap: "wrap" }}>
          <TextField
            size="small"
            placeholder="Search customers…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            sx={{ flex: "0 0 300px" }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Box sx={{ color: c.faint2, display: "flex" }}>
                    <Icon name="search" size={16} stroke={1.8} />
                  </Box>
                </InputAdornment>
              ),
            }}
          />
          <Segmented
            value={healthFilter}
            onChange={setHealthFilter}
            options={[
              { key: "all", label: "All" },
              { key: "green", label: "Healthy" },
              { key: "amber", label: "Aging" },
              { key: "red", label: "At risk" },
            ]}
          />
          <Box sx={{ ml: "auto", fontFamily: brainTokens.font.mono, fontSize: 12, color: c.faint2 }}>
            {filtered.length} / {customers.length}
          </Box>
        </Box>

        <TableContainer sx={{ borderTop: `1px solid ${c.borderLight}` }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Customer</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Projects</TableCell>
                <TableCell sx={{ width: 200 }}>Completeness</TableCell>
                <TableCell align="right">Last activity</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} sx={{ color: c.muted2, py: 4, textAlign: "center" }}>
                    Loading customers…
                  </TableCell>
                </TableRow>
              ) : sorted.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} sx={{ color: c.muted2, py: 4, textAlign: "center" }}>
                    No customers match your search.
                  </TableCell>
                </TableRow>
              ) : (
                sorted.map((cu) => {
                  const activeProjects = (projectMap.get(cu.customerId) ?? []).filter((p) => isOpenProjectStatus(p.status)).length;
                  return (
                    <TableRow
                      key={cu.customerId}
                      hover
                      onClick={() => navigate(`/customers/${encodeURIComponent(cu.customerId)}`)}
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: "11px" }}>
                          <CustomerAvatar name={cu.name} size={34} />
                          <Box>
                            <Box sx={{ fontWeight: 500, color: c.ink }}>{cu.name}</Box>
                            {cu.tier && <Box sx={{ fontSize: 12, color: c.muted2, mt: "1px" }}>{cu.tier}</Box>}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <StatusPill status={cu.status} label={cu.status} dot />
                      </TableCell>
                      <TableCell align="right">
                        {activeProjects > 0 ? (
                          <Box component="span" sx={{ fontFamily: brainTokens.font.mono, fontSize: 13 }}>
                            {activeProjects}
                          </Box>
                        ) : (
                          <Box component="span" sx={{ color: c.placeholder }}>—</Box>
                        )}
                      </TableCell>
                      <TableCell>
                        {cu.completeness == null ? (
                          <Box component="span" sx={{ color: c.placeholder }}>—</Box>
                        ) : (
                          <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <Box sx={{ flex: 1, minWidth: 60, height: 5, borderRadius: "999px", background: "#eef1f3", overflow: "hidden" }}>
                              <Box sx={{ height: "100%", width: `${Math.min(100, Math.max(0, cu.completeness))}%`, background: c.tealInk2, borderRadius: "999px" }} />
                            </Box>
                            <Box component="span" sx={{ fontFamily: brainTokens.font.mono, fontSize: 12, color: c.muted, width: 34, textAlign: "right" }}>
                              {Math.round(cu.completeness)}%
                            </Box>
                          </Box>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Box component="span" sx={{ fontSize: 13, color: activityColor(cu.updatedAt ?? cu.createdAt) }}>
                          {relDate(cu.updatedAt ?? cu.createdAt)}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </SectionCard>

      <Dialog open={newOpen} onClose={() => setNewOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>New customer</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Name"
            value={newName}
            placeholder="Helicopter Maintenance NZ"
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") void createCustomer();
            }}
            helperText="Creates the record, then opens it so you can fill in tier, product, owner, and domains."
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewOpen(false)} sx={{ color: c.ink3 }}>
            Cancel
          </Button>
          <Button variant="contained" disabled={busy || !newName.trim()} onClick={() => void createCustomer()}>
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
